// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Button, Container, PasswordInput, Stack, Title } from '@mantine/core';
import { normalizeErrorString } from '@medplum/core';
import { useMedplum } from '@medplum/react';
import type { JSX } from 'react';
import { useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';

export function SetPasswordPage(): JSX.Element {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id') ?? '';
  const secret = searchParams.get('secret') ?? '';
  const isReset = searchParams.get('mode') === 'reset';
  const heading = isReset ? 'Reset your password' : 'Set Your Password';
  const submitLabel = isReset ? 'Reset Password' : 'Set Password';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!id || !secret) {
    return <Navigate to={`/invite-expired${isReset ? '?mode=reset' : ''}`} replace />;
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setLoading(true);
    try {
      await medplum.post('auth/setpassword', { id, secret, password: newPassword });
      await navigate('/signin');
    } catch (err) {
      console.error('[SetPasswordPage] auth/setpassword failed', err);
      if (isReset) {
        await navigate('/invite-expired?mode=reset');
        return;
      }
      setError(normalizeErrorString(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Container size="xs" mt="xl">
      <Stack gap="md">
        <Title order={2}>{heading}</Title>

        {error && (
          <Alert color="red" title="Error">
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack gap="sm">
            <PasswordInput
              label="New password"
              placeholder="Enter your new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.currentTarget.value)}
              required
            />
            <PasswordInput
              label="Confirm password"
              placeholder="Confirm your new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.currentTarget.value)}
              required
            />
            <Button type="submit" loading={loading} fullWidth>
              {submitLabel}
            </Button>
          </Stack>
        </form>
      </Stack>
    </Container>
  );
}
